import {
  RelativeDatePeriod,
  RelativeDateUnit,
  type RelativeDateValue,
} from '@ardoq/api-types';
import {
  addWeeks,
  endOfDay,
  endOfISOWeek,
  getDaysInMonth,
  startOfDay,
  startOfISOWeek,
  subWeeks,
} from 'date-fns';
import { constructDate, copyDate, currentDate } from './dateOperations';

const calculateThisWeek = (now: Date) => ({
  start: startOfISOWeek(now),
  end: endOfISOWeek(now),
});

const calculateThisMonth = (now: Date) => {
  const year = now.getFullYear();
  const month = now.getMonth();

  return {
    start: constructDate(year, month, 1),
    end: endOfDay(constructDate(year, month + 1, 0)),
  };
};

const calculateThisQuarter = (now: Date) => {
  const year = now.getFullYear();
  const month = now.getMonth();
  const quarterStartMonth = Math.floor(month / 3) * 3;

  return {
    start: constructDate(year, quarterStartMonth, 1),
    end: endOfDay(constructDate(year, quarterStartMonth + 3, 0)),
  };
};

const calculateThisYear = (now: Date) => {
  const year = now.getFullYear();

  return {
    start: constructDate(year, 0, 1),
    end: endOfDay(constructDate(year, 11, 31)),
  };
};

const calculateLastWeek = (now: Date) => {
  const lastWeek = subWeeks(now, 1);

  return {
    start: startOfISOWeek(lastWeek),
    end: endOfISOWeek(lastWeek),
  };
};

const calculateLastMonth = (now: Date) => {
  const year = now.getFullYear();
  const month = now.getMonth();

  return {
    start: constructDate(year, month - 1, 1),
    end: endOfDay(constructDate(year, month, 0)),
  };
};

const calculateLastQuarter = (now: Date) => {
  const year = now.getFullYear();
  const month = now.getMonth();
  const quarterStartMonth = Math.floor(month / 3) * 3;

  return {
    start: constructDate(year, quarterStartMonth - 3, 1),
    end: endOfDay(constructDate(year, quarterStartMonth, 0)),
  };
};

const calculateLastYear = (now: Date) => {
  const year = now.getFullYear();

  return {
    start: constructDate(year - 1, 0, 1),
    end: endOfDay(constructDate(year - 1, 11, 31)),
  };
};

const calculateNextWeek = (now: Date) => {
  const nextWeek = addWeeks(now, 1);

  return {
    start: startOfISOWeek(nextWeek),
    end: endOfISOWeek(nextWeek),
  };
};

const calculateNextMonth = (now: Date) => {
  const year = now.getFullYear();
  const month = now.getMonth();

  return {
    start: constructDate(year, month + 1, 1),
    end: endOfDay(constructDate(year, month + 2, 0)),
  };
};

const calculateNextQuarter = (now: Date) => {
  const year = now.getFullYear();
  const month = now.getMonth();
  const quarterStartMonth = Math.floor(month / 3) * 3;

  return {
    start: constructDate(year, quarterStartMonth + 3, 1),
    end: endOfDay(constructDate(year, quarterStartMonth + 6, 0)),
  };
};

const calculateNextYear = (now: Date) => {
  const year = now.getFullYear();

  return {
    start: constructDate(year + 1, 0, 1),
    end: endOfDay(constructDate(year + 1, 11, 31)),
  };
};

/**
 * Returns the date range [now − amount, now] for the given unit.
 *
 * For MONTH and QUARTER units, the start date is clamped to the last valid day
 * of the target month (e.g. March 31 − 1 month = Feb 28). As a consequence,
 * a single N-month subtraction is not equivalent to N successive 1-month
 * subtractions. Always express the full interval as one call.
 */
const calculateInThePast = (
  now: Date,
  unit: RelativeDateUnit,
  amount: number
): { start: Date; end: Date } | null => {
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const currentDay = now.getDate();

  const startDate = copyDate(now);
  const endDate = copyDate(now);

  switch (unit) {
    case RelativeDateUnit.DAY:
      startDate.setDate(currentDay - amount);
      break;
    case RelativeDateUnit.WEEK:
      startDate.setDate(currentDay - amount * 7);
      break;
    case RelativeDateUnit.MONTH:
      startDate.setDate(1);
      startDate.setMonth(currentMonth - amount);
      startDate.setDate(Math.min(currentDay, getDaysInMonth(startDate)));
      break;
    case RelativeDateUnit.QUARTER:
      startDate.setDate(1);
      startDate.setMonth(currentMonth - amount * 3);
      startDate.setDate(Math.min(currentDay, getDaysInMonth(startDate)));
      break;
    case RelativeDateUnit.YEAR:
      startDate.setFullYear(currentYear - amount);
      break;
    default:
      return null;
  }

  return {
    start: startOfDay(startDate),
    end: endOfDay(endDate),
  };
};

/**
 * Returns the date range [now, now + amount] for the given unit.
 *
 * For MONTH and QUARTER units, the end date is clamped to the last valid day
 * of the target month (e.g. Jan 31 + 1 month = Feb 28). As a consequence,
 * a single N-month addition is not equivalent to N successive 1-month
 * additions. Always express the full interval as one call.
 */
const calculateInTheNext = (
  now: Date,
  unit: RelativeDateUnit,
  amount: number
): { start: Date; end: Date } | null => {
  const currentMonth = now.getMonth();
  const currentDay = now.getDate();
  const currentYear = now.getFullYear();

  const startDate = copyDate(now);
  const endDate = copyDate(now);

  switch (unit) {
    case RelativeDateUnit.DAY:
      endDate.setDate(currentDay + amount);
      break;
    case RelativeDateUnit.WEEK:
      endDate.setDate(currentDay + amount * 7);
      break;
    case RelativeDateUnit.MONTH:
      endDate.setDate(1);
      endDate.setMonth(currentMonth + amount);
      endDate.setDate(Math.min(currentDay, getDaysInMonth(endDate)));
      break;
    case RelativeDateUnit.QUARTER:
      endDate.setDate(1);
      endDate.setMonth(currentMonth + amount * 3);
      endDate.setDate(Math.min(currentDay, getDaysInMonth(endDate)));
      break;
    case RelativeDateUnit.YEAR:
      endDate.setFullYear(currentYear + amount);
      break;
    default:
      return null;
  }

  return {
    start: startOfDay(startDate),
    end: endOfDay(endDate),
  };
};

type DateRangeCalculator = (now: Date) => { start: Date; end: Date };

const calculators: Record<
  RelativeDatePeriod.THIS | RelativeDatePeriod.LAST | RelativeDatePeriod.NEXT,
  Partial<Record<RelativeDateUnit, DateRangeCalculator>>
> = {
  [RelativeDatePeriod.THIS]: {
    [RelativeDateUnit.WEEK]: calculateThisWeek,
    [RelativeDateUnit.MONTH]: calculateThisMonth,
    [RelativeDateUnit.QUARTER]: calculateThisQuarter,
    [RelativeDateUnit.YEAR]: calculateThisYear,
  },
  [RelativeDatePeriod.LAST]: {
    [RelativeDateUnit.WEEK]: calculateLastWeek,
    [RelativeDateUnit.MONTH]: calculateLastMonth,
    [RelativeDateUnit.QUARTER]: calculateLastQuarter,
    [RelativeDateUnit.YEAR]: calculateLastYear,
  },
  [RelativeDatePeriod.NEXT]: {
    [RelativeDateUnit.WEEK]: calculateNextWeek,
    [RelativeDateUnit.MONTH]: calculateNextMonth,
    [RelativeDateUnit.QUARTER]: calculateNextQuarter,
    [RelativeDateUnit.YEAR]: calculateNextYear,
  },
};

/**
 * Calculates the start and end dates for a relative date period.
 *
 * @param relativeDateValue - The relative date configuration
 * @returns An object with start and end dates, or null if invalid
 */
export const getRelativeDateRange = (
  relativeDateValue: RelativeDateValue
): { start: Date; end: Date } | null => {
  const now = currentDate();
  const { period, unit } = relativeDateValue;

  if (period === RelativeDatePeriod.IN_THE_PAST) {
    if (!('amount' in relativeDateValue)) return null;
    return calculateInThePast(now, unit, relativeDateValue.amount);
  }

  if (period === RelativeDatePeriod.IN_THE_NEXT) {
    if (!('amount' in relativeDateValue)) return null;
    return calculateInTheNext(now, unit, relativeDateValue.amount);
  }

  const calculator = calculators[period]?.[unit];
  return calculator ? calculator(now) : null;
};
